import React, {useState, useMemo} from 'react';
import MDBox from "materialDashboard/components/MDBox";
import MDTypography from "materialDashboard/components/MDTypography";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import {useTranslation} from "react-i18next";

const LegendControl = ({webgis}) => {
    const {t} = useTranslation(["webgis"]);
    const {layersState, featuresState} = webgis;
    const {layers} = layersState;
    const {webgisRegistryQuery} = featuresState;

    const [isOpen, setIsOpen] = useState(false);
    const [selectedLayerKey, setSelectedLayerKey] = useState(null);

    const registry = webgisRegistryQuery.data ?? {};

    // Mappa tra i nomi dei layer nello stato e le chiavi nel registro del backend
    const layerMapping = {
        model1Stations: "models1",
        models23: "models23",
        incidents: "incidents",
        microzones: "microzones",
        stations: "stations"
    };

    // Layer attivi che hanno almeno una legenda nel registro
    const activeLayersWithLegends = useMemo(() => {
        return Object.keys(layers)
            .filter(key => layers[key].active)
            .filter(key => {
                const registryKey = layerMapping[key];
                const layerRegistry = registry[registryKey];
                return layerRegistry && layerRegistry.params && layerRegistry.params.length > 0;
            });
    }, [layers, registry]);

    // Imposta il primo layer attivo con legenda se non selezionato
    useMemo(() => {
        if (activeLayersWithLegends.length > 0 && (!selectedLayerKey || !activeLayersWithLegends.includes(selectedLayerKey))) {
            setSelectedLayerKey(activeLayersWithLegends[0]);
        }
    }, [activeLayersWithLegends]);

    if (activeLayersWithLegends.length === 0) return null;

    const currentLayerKey = selectedLayerKey || activeLayersWithLegends[0];
    const registryKey = layerMapping[currentLayerKey];
    const layerRegistry = registry[registryKey];

    // Prendi il parametro visibile dal layer state, altrimenti usa il primo disponibile
    const visibleParamCode = layers[currentLayerKey].visibleParam || (layerRegistry?.params?.[0]?.code);
    const legendItems = layerRegistry?.legends?.[visibleParamCode] || [];
    const currentParam = layerRegistry?.params?.find(p => p.code === visibleParamCode);

    return (
        <div className="leaflet-control-container">
            <div className="leaflet-control leaflet-control-layers"
                 style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                {isOpen && (
                    <MDBox sx={{
                        p: 1,
                        minWidth: 200,
                        maxWidth: 300,
                        maxHeight: '400px',
                        overflowY: 'auto'
                    }}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <MDTypography variant="h6" fontWeight="medium">
                                {t("legend", "Legenda")}
                            </MDTypography>
                            <ButtonBase
                                onClick={() => setIsOpen(false)}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    minWidth: 24,
                                    borderRadius: '4px',
                                    '&:hover': {bgcolor: 'grey.100'}
                                }}
                            >
                                <Icon fontSize="small">close</Icon>
                            </ButtonBase>
                        </MDBox>

                        {activeLayersWithLegends.length > 1 && <FormControl fullWidth size="small" sx={{mb: 2}}>
                            <Select
                                variant="standard"
                                labelId="legend-layer-select-label"
                                value={currentLayerKey}
                                onChange={(e) => setSelectedLayerKey(e.target.value)}
                                sx={{height: '40px'}}
                            >
                                {activeLayersWithLegends.map(key => (
                                    <MenuItem key={key} value={key}>
                                        {registry[layerMapping[key]]?.description ?? key}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        }

                        {currentParam && (
                            <MDTypography variant="caption" fontWeight="bold" display="block" mb={1}
                                          sx={{textTransform: 'uppercase'}}>
                                {currentParam.description}
                            </MDTypography>
                        )}

                        <MDBox>
                            {legendItems.map((item, index) => (
                                <MDBox key={index} display="flex" alignItems="center" mb={0.5}>
                                    <MDBox
                                        sx={{
                                            width: 16,
                                            height: 16,
                                            bgcolor: item.color,
                                            borderRadius: '6px',
                                            mr: 1,
                                            border: '1px solid rgba(0,0,0,0.1)'
                                        }}
                                    />
                                    <MDTypography variant="button" fontWeight="regular" color="text">
                                        {item.description}
                                    </MDTypography>
                                </MDBox>
                            ))}
                            {legendItems.length === 0 && (
                                <MDTypography variant="caption" color="text" italic>
                                    {t("noLegendAvailable", "Nessuna legenda disponibile per questo parametro")}
                                </MDTypography>
                            )}
                        </MDBox>
                    </MDBox>
                )}

                {!isOpen && <ButtonBase
                    onClick={() => setIsOpen(!isOpen)}
                    sx={{
                        width: 42,
                        height: 42,
                        bgcolor: 'background.paper',
                        borderRadius: '4px',
                        boxShadow: 2,
                        '&:hover': {bgcolor: 'grey-100'}
                    }}
                >
                    <Icon fontSize="medium">{isOpen ? 'legend_toggle' : 'legend_toggle'}</Icon>
                </ButtonBase>
                }
            </div>
        </div>
    );
};

export default LegendControl;
